import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box, Paper, Typography, MenuItem, FormControl, Select, InputLabel, Grid, TextField
} from "@mui/material";
import { useDispatch, useSelector } from 'react-redux'
import ButtonMain from "../Button";
import httpClient from '../../_util/api';
import { hideLoader, showLoader } from '../../Store/mainSlice'
import CustomizedSnackbars from "../../shared/Snackbar/SnackBar";
import { downloadCSV } from "./downloadCSV";

const FACULTIES = [
  "Architecture & Planning",
  "Business Management",
  "Computing & IT",
  "Education",
  "Engineering & Technology",
  "Fashion, Textile & Media Studies",
  "Health & Medical Sciences",
  "LAW",
  "Life Sciences",
  "Science & Technology",
  "Social Sciences",
  "Misc.",
  "Other",
];

const GRADUATION_FORECAST = "Graduation Forecast (Faculty-wise)";

const allReportOptions = {
  "Application Reports": [
    "Total Received Applications",
    "Reviewed Applications",
    "Approved Applications",
    "Rejected Applications",
    "Final Semester Students Report",
    "Graduated Students Report",
    "High Achievers Report",
    "Multiple Scholarship Recipients Report"
  ],
  "Financial Reports": [
    "Fund Allocation Report",
    "Reimbursement Report",
    "Pending Disbursement Report"
  ],
  "Partner-wise Report": [],
  "Faculty-wise Report": FACULTIES,
  [GRADUATION_FORECAST]: ["All Faculties", ...FACULTIES],
  "Operational Reports": [
    "Scholarship Disbursement Report",
    "Yearly Scholarship Summary",
    "Student Record Keeping Report"
  ],
  "Admin & Audit Reports": [
    "Approver Activity Log",
    "System Usage Report",
    "Missing Documents Report"
  ]
};

const formatDate = (input) => {
  if (!input) return "";
  const [year, month, day] = input.split('-').map(Number);
  const localDate = new Date(year, month - 1, day);
  const formatted = localDate.toLocaleDateString('en-US');
  return formatted;
};

const ReportDashboard = () => {
  const dispatch = useDispatch();
  const role = useSelector((state) => state.mainSlice.userdetail?.role);
  const snackbar_Ref = useRef(null); 
  const [mainCategory, setMainCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [vendorList, setVendorList] = useState([]);

  // Hide Admin & Audit Reports and Financial Reports from approver
  const reportOptions = useMemo(() => {
    if (role !== "reviewer") return allReportOptions;
    const filtered = { ...allReportOptions };
    delete filtered["Admin & Audit Reports"];
    delete filtered["Financial Reports"];
    return filtered;
  }, [role]);

  const endpoints = {
    "Application Reports": "/report/applicationSummary",
    "Faculty-wise Report": "/report/facultySummary",
    [GRADUATION_FORECAST]: "/report/graduationForecast",
    "Financial Reports": "/report/financialSummary",
    "Partner-wise Report": `/report/vendorSummary/${subCategory}`,
    "Operational Reports": "/report/applicationSummary",
    "Admin & Audit Reports": "/report/adminAuditSummary"
  };

  const handleDownload = async () => {
    if (!mainCategory) {
      snackbar_Ref.current.showMessage("warning", "Please select a main category first.", "", "i-info-circle");
      return;
    }
    dispatch(showLoader());
    const filter = {
      subCategory,
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
    };

    try {
      const endpoint = endpoints[mainCategory];
      if (!endpoint) throw new Error("No endpoint configured for this main category.");

      const res = await httpClient.post(endpoint, filter);

      dispatch(hideLoader());
      if (res.status === 200) {
        downloadCSV(res.data.data, mainCategory)
        snackbar_Ref.current.showMessage("success", res.data.message, "", "i-check-circle");
      } else {
        snackbar_Ref.current.showMessage("error", "Failed to download report.", "", "i-error-circle");
      }
    } catch (error) {
      dispatch(hideLoader());
      snackbar_Ref.current.showMessage("error", error?.response?.data?.message || error.message || "Something went wrong.", "", "i-error-circle");
    }
  };

  const getVendorListHandler = async () => {
    try {
      const res = await httpClient.get(`/user/vendors`);
      if (res.status === 200) {
        setVendorList(res.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    getVendorListHandler();
  }, []);

  useEffect(() => {
    if (mainCategory && !Object.prototype.hasOwnProperty.call(reportOptions, mainCategory)) {
      setMainCategory("");
      setSubCategory("");
    } else if (
      mainCategory &&
      subCategory &&
      mainCategory !== "Partner-wise Report" &&
      reportOptions[mainCategory] &&
      !reportOptions[mainCategory].includes(subCategory)
    ) {
      // Partner-wise Report's sub category is a vendor _id from vendorList,
      // not one of the static reportOptions labels, so it must be excluded
      // from this check — otherwise it gets reset to "" right after selection.
      setSubCategory("");
    }
  }, [reportOptions, mainCategory, subCategory]);
  
  return (
    <>
      <CustomizedSnackbars ref={snackbar_Ref} />
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2, pr: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          Reports Overview
        </Typography>
        <ButtonMain onClick={handleDownload} disabled={!mainCategory || !subCategory}>
          Download Report
        </ButtonMain>
      </Box>
      <Paper sx={{ p: 3, mt: 2 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Main Category</InputLabel>
                  <Select
                    value={mainCategory}
                    onChange={(e) => {
                      setMainCategory(e.target.value);
                      setSubCategory("");
                    }}
                    label="Main Category"
                  >
                    {Object.keys(reportOptions).map((main) => (
                      <MenuItem key={main} value={main}>{main}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={mainCategory === GRADUATION_FORECAST ? "Graduating From" : "From Date"}
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth disabled={!mainCategory}>
                  <InputLabel>Sub Category</InputLabel>
                  {mainCategory == "Partner-wise Report" ? (
                    <Select
                      value={subCategory}
                      onChange={(e) => setSubCategory(e.target.value)}
                      label="Sub Category"
                    >
                      {vendorList?.map((sub) => (
                        <MenuItem key={sub._id} value={sub._id}>{sub.firstName}</MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Select
                      value={subCategory}
                      onChange={(e) => setSubCategory(e.target.value)}
                      label="Sub Category"
                    >
                      {reportOptions[mainCategory]?.map((sub) => (
                        <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                      ))}
                    </Select>
                  )}  
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={mainCategory === GRADUATION_FORECAST ? "Graduating To" : "To Date"}
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  fullWidth
                  helperText={mainCategory === GRADUATION_FORECAST
                    ? "Estimated from each student's latest semester (8 semesters, 10 for Health & Medical Sciences; 6 months each)"
                    : ""}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default ReportDashboard;
